export const REQUEST_ERROR_MESSAGE = 'API returned a server error';

const UNKNOWN_ROUTE = 'unknown';
const UNKNOWN_METHOD = 'UNKNOWN';
const UNKNOWN_STATUS = 'unknown';
const ID_SEGMENT = ':id';

/**
 * Word-like segments are not safe to keep: routes such as /portfolio/:accountId
 * interpolate caller-supplied values that can be a name or handle.
 * Action type still separates issues. Only infrastructure segments and
 * unresolved :placeholders survive.
 */
const STATIC_SEGMENT = /^(?:api|apis|v\d+|:[a-z_]+)$/i;

type RequestFailureBody = {
	errors?: unknown;
	message?: unknown;
	description?: unknown;
	msg?: unknown;
};

type RequestFailure = {
	actionType: string;
	status: unknown;
	method: string | undefined;
	path: string | undefined;
};

export type MiddlewareRequestErrorContext = {
	requestAction: string;
	requestMethod: string;
	requestRoute: string;
	httpStatus?: number;
};

export const requestRouteTemplate = (path: string): string => {
	const pathOnly = path.split(/[?#]/)[0];
	if (pathOnly.length === 0) {
		return UNKNOWN_ROUTE;
	}
	return pathOnly
		.split('/')
		.map((segment) =>
			segment.length === 0 || STATIC_SEGMENT.test(segment) ? segment : ID_SEGMENT
		)
		.join('/');
};

const serializedMessage = (value: unknown): string | undefined => {
	if (typeof value === 'string') {
		return value.length > 0 ? value : undefined;
	}
	if (Array.isArray(value)) {
		if (value.length === 0) return undefined;
	} else if (value !== null && typeof value === 'object') {
		if (Object.keys(value).length === 0) return undefined;
	} else {
		return undefined;
	}
	try {
		const serialized = JSON.stringify(value);
		return serialized && serialized !== '[]' && serialized !== '{}' ? serialized : undefined;
	} catch {
		return undefined;
	}
};

export const middlewareRequestErrorMessage = (
	response: RequestFailureBody | null | undefined
): string => {
	const candidates = [response?.errors, response?.message, response?.description, response?.msg];
	for (const candidate of candidates) {
		const message = serializedMessage(candidate);
		if (message) return message;
	}
	return REQUEST_ERROR_MESSAGE;
};

/**
 * Datadog groups browser errors by message and stack, and every middleware
 * failure shares one throw site. The context feeds `dd_context`, and its
 * fields build the `dd_fingerprint` that splits failures by request.
 */
export const middlewareRequestErrorContext = ({
	actionType,
	status,
	method,
	path,
}: RequestFailure): MiddlewareRequestErrorContext => {
	const context: MiddlewareRequestErrorContext = {
		requestAction: actionType,
		requestMethod: method ? method.toUpperCase() : UNKNOWN_METHOD,
		requestRoute: path ? requestRouteTemplate(path) : UNKNOWN_ROUTE,
	};
	if (typeof status === 'number') {
		context.httpStatus = status;
	}
	return context;
};

export const middlewareRequestErrorFingerprint = ({
	requestAction,
	requestMethod,
	requestRoute,
	httpStatus,
}: MiddlewareRequestErrorContext): string =>
	[
		'middleware-request',
		requestAction,
		httpStatus ?? UNKNOWN_STATUS,
		requestMethod,
		requestRoute,
	].join(':');
